// src/components/common/SectionHeader.tsx
'use client';

import React from 'react';
import Link from 'next/link';

export interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  descriptions?: string[];
  badge?: string;
  href?: string;
  className?: string;
  center?: boolean;
}

export default function SectionHeader({
  title,
  subtitle,
  descriptions,
  badge,
  href,
  className = '',
  center = true,
}: SectionHeaderProps) {
  const TitleEl = (
    // NOTE: no text-shadow here — text-shadow is incompatible with
    // background-clip:text (see the .juicy-title comment in globals.css); the
    // shadow paints through the transparent glyphs and muddies the gradient.
    // .juicy-title is the one token-driven heading gradient.
    <h2 className="font-js juicy-title text-4xl md:text-5xl font-extrabold leading-[1.2]">
      {title}
    </h2>
  );

  return (
    <div className={[center ? 'text-center' : '', 'my-3 md:my-4', className].join(' ').trim()}>
      <div className="inline-flex items-center gap-2">
        {badge && (
          <span className="inline-flex items-center rounded-full bg-primary/15 text-primary border border-primary/30 px-2 py-0.5 text-xs font-semibold">
            {badge}
          </span>
        )}
        {href ? (
          <Link href={href} className="hover:opacity-95">
            {TitleEl}
          </Link>
        ) : (
          TitleEl
        )}
      </div>
      {subtitle && (
        <p className="mt-2 text-base md:text-lg text-muted-foreground leading-relaxed">{subtitle}</p>
      )}
      {Array.isArray(descriptions) && descriptions.length > 0 && (
        <div className="mt-3 space-y-2">
          {descriptions.map((d, i) => (
            <p key={i} className="text-[1.1rem] text-muted-foreground leading-relaxed">{d}</p>
          ))}
        </div>
      )}
    </div>
  );
}
